// Strategy for handling carbon calculator questions and answers.
// Provides helpers for extracting question data, building tree structure, and answer search.
// Used by SharedAnswers and other calculator components for consistent logic.

// calculatorStrategies/carbonStrategy.js
import { CALC_MODELS } from '../../../constants';
import {
  getCustomCO2QuestionData,
  addCustomCO2ToStructure,
  getCustomCO2AnswersStructure,
} from './customCO2Strategy';

const getCompositeId = (question) =>
  `${question?.data?.categoryCode ?? CALC_MODELS.CUSTOM_CO2}_${question?.outerId ?? ''}`;

const getQuestionData = (sharedQuestionAnswers, contextQuestions) => {
  if (!sharedQuestionAnswers || !contextQuestions?.[CALC_MODELS.CARBON]) return {};

  const shareQuestions = sharedQuestionAnswers.reduce(
    (acc, share) => [...acc, ...share.questions],
    []
  );

  const newData = shareQuestions.reduce((acc, shareQuestion) => {
    if (shareQuestion.type === CALC_MODELS.CUSTOM_CO2) {
      return {
        ...acc,
        ...getCustomCO2QuestionData(shareQuestion),
      };
    }
    const newScopeIdx = contextQuestions[CALC_MODELS.CARBON].findIndex(
      (scope) => scope.code === shareQuestion.data.scopeCode
    );
    const newScope = contextQuestions[CALC_MODELS.CARBON][newScopeIdx];
    const newCategoryIdx = newScope.categories.findIndex(
      (category) => category.code === shareQuestion.data.categoryCode
    );
    const newCategory = newScope.categories[newCategoryIdx];
    const newQuestionIdx = newCategory.questions.findIndex(
      (question) => question.id === shareQuestion.outerId
    );
    const newQuestion = newCategory.questions[newQuestionIdx];

    return {
      ...acc,
      [getCompositeId(shareQuestion)]: {
        scopeName: newScope.name,
        scopeIdx: newScopeIdx,
        categoryName: newCategory.name,
        categoryIdx: newCategoryIdx,
        questionName: newQuestion.name,
        questionIdx: newQuestionIdx,
      },
    };
  }, {});
  return newData;
};

const getStructure = (acc, question) => {
  if (question.type === CALC_MODELS.CUSTOM_CO2) {
    return addCustomCO2ToStructure(acc, question);
  }

  const { categoryCode, scopeCode, scopeName, categoryName, scopeIdx, categoryIdx, questionIdx } =
    question?.data ?? {};

  const scope = acc.find((el) => el.code === scopeCode) ?? {
    code: scopeCode,
    name: scopeName,
    type: CALC_MODELS.CARBON,
    order: scopeIdx,
    sub: [],
  };
  const category = (scope.sub || []).find((el) => el.code === categoryCode) ?? {
    code: categoryCode,
    name: categoryName,
    order: categoryIdx,
    children: [],
  };

  const newCategory = {
    ...category,
    children: [
      ...category.children.filter((el) => el.id !== question.id),
      { ...question, order: questionIdx },
    ].sort((a, b) => a.order - b.order),
  };

  const newScope = {
    ...scope,
    sub: [...(scope.sub || []).filter((el) => el.code !== categoryCode), newCategory].sort(
      (a, b) => a.order - b.order
    ),
  };

  return [...acc.filter((el) => el.code !== scopeCode), newScope].sort((a, b) => a.order - b.order);
};

const getQuestionsSearchStrings = (question) => ({
  compositeId: question.compositeId,
  strings: [question.data?.questionName ?? ''],
});

const isAnswerExists = (question, contextAnswers) => {
  const { outerId, data } = question;
  const { categoryCode } = data;
  const answer = contextAnswers?.[categoryCode]?.[outerId];
  return !!answer;
};

// const getAnswerSearchStrings = (sharedAnswersCollection) =>
//   Object.keys(sharedAnswersCollection).map((key) => ({
//     id: key,
//     answers: sharedAnswersCollection[key].map(({ id, data }) => {
//       const metaStrings = data?.data?.reduce((acc, el) => {
//         const meta = Object.values(el.meta || {}).filter(
//           (val) => val && val !== '' && val !== 'undefined'
//         );
//         return [...acc, ...meta];
//       }, []);
//       return { id, strings: [...new Set(metaStrings)] };
//     }),
//   }));

const getAnswersStructure = (question, accType, sharedAnswersCollection, selectedAnswerIds) => {
  if (question.type === CALC_MODELS.CUSTOM_CO2) {
    return getCustomCO2AnswersStructure(
      question,
      accType,
      sharedAnswersCollection,
      selectedAnswerIds
    );
  }

  const { categoryCode } = question?.data ?? {};
  const answer = sharedAnswersCollection[question.compositeId].find(
    (a) => a.id === selectedAnswerIds[question.compositeId]
  );
  return {
    ...accType,
    [categoryCode]: {
      ...accType[categoryCode],
      [question.outerId]: [...answer.data],
    },
  };
};

export const carbonStrategy = {
  getQuestionData,
  getStructure,
  getCompositeId,
  isAnswerExists,
  getAnswersStructure,
  getQuestionsSearchStrings,
  // getAnswerSearchStrings,getQuestionData
  type: CALC_MODELS.CARBON,
};
